var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Color, MeshPhysicalMaterial, MeshStandardMaterial, ShaderMaterial } from 'three';
import { assertUnreachable } from '@canvas-logic/clp-utils';
export const MATERIAL_MAP_NAMES = [
    'map',
    'metalnessMap',
    'roughnessMap',
    'alphaMap',
    'aoMap',
    'bumpMap',
    'displacementMap',
    'emissiveMap',
    'envMap',
    'lightMap',
    'normalMap',
];
export const STANDARD_MATERIAL_OPTIONS = ['roughness', 'color', 'metalness', 'envMapIntensity', ...MATERIAL_MAP_NAMES];
export const PHYSICAL_MATERIAL_OPTIONS = ['clearcoat', 'clearcoatRoughness', ...STANDARD_MATERIAL_OPTIONS];
export class MaterialsLibrary {
    constructor(materialLoader, textureLibrary) {
        this.materialLoader = materialLoader;
        this.textureLibrary = textureLibrary;
        this.specsById = {};
        this.materialsById = {};
    }
    addMaterials(materialAssets) {
        for (const materialAsset of materialAssets) {
            this.specsById[materialAsset.id] = materialAsset;
        }
    }
    get(id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (id in this.materialsById) {
                return this.materialsById[id];
            }
            const spec = this.specsById[id];
            if (!spec) {
                throw new Error(`Cannot find material "${id}" in a library`);
            }
            yield this.createMaterial(spec);
            return this.materialsById[id];
        });
    }
    createMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            let material;
            switch (spec.type) {
                case 'meshMaterialAsset':
                    material = yield this.createMeshMaterial(spec);
                    break;
                case 'shaderMaterialAsset':
                    material = yield this.createShaderMaterial(spec);
                    break;
                case 'standardMaterialAsset':
                    material = yield this.createStandardMaterial(spec);
                    break;
                case 'physicalMaterialAsset':
                    material = yield this.createPhysicalMaterial(spec);
                    break;
                default:
                    assertUnreachable(spec, 'Invalid spec');
            }
            if (material === undefined) {
                throw new Error(`Cannot find material "${spec.id}"`);
            }
            this.materialsById[spec.id] = material;
        });
    }
    createMeshMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.materialLoader.get(spec);
        });
    }
    createShaderMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            const specUniforms = Object.entries(spec.uniforms);
            const uniforms = {};
            yield Promise.all(specUniforms.map((_a) => __awaiter(this, [_a], void 0, function* ([textureName, textureId]) {
                uniforms[textureName] = { value: yield this.textureLibrary.get(textureId) };
            })));
            const material = new ShaderMaterial({
                fragmentShader: spec.fragmentShader,
                vertexShader: spec.vertexShader,
                transparent: spec.transparent,
                uniforms,
            });
            material.needsUpdate = true;
            material.name = spec.id;
            return material;
        });
    }
    createStandardMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            const maps = yield this.loadTexturesMaps(spec);
            const material = new MeshStandardMaterial(Object.assign({ color: spec.color, roughness: spec.roughness, metalness: spec.metalness, envMapIntensity: spec.envMapIntensity }, maps));
            return material;
        });
    }
    loadTexturesMaps(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            const mapKeys = [];
            const mapValues = [];
            for (const mapName of MATERIAL_MAP_NAMES) {
                if (spec[mapName]) {
                    mapKeys.push(mapName);
                    mapValues.push(this.textureLibrary.get(spec[mapName]));
                }
            }
            const values = yield Promise.all(mapValues);
            const result = {};
            for (let i = 0; i < values.length; i++) {
                const mapName = mapKeys[i];
                const value = values[i];
                if (!value) {
                    throw new Error(`Cannot load texture for ${mapName} with id ${spec[mapName]}`);
                }
                result[mapName] = value;
            }
            return result;
        });
    }
    updateMaterial(materialSpec) {
        return __awaiter(this, void 0, void 0, function* () {
            const material = yield this.get(materialSpec.id);
            const type = materialSpec.type;
            switch (type) {
                case 'standardMaterialAsset':
                    if (!(material instanceof MeshStandardMaterial)) {
                        throw new Error(`Invalid material specification for ${materialSpec.id}`);
                    }
                    yield this.updateMaterialProperties(materialSpec, material, STANDARD_MATERIAL_OPTIONS);
                    break;
                case 'physicalMaterialAsset':
                    if (!(material instanceof MeshPhysicalMaterial)) {
                        throw new Error(`Invalid material specification for ${materialSpec.id}`);
                    }
                    yield this.updateMaterialProperties(materialSpec, material, PHYSICAL_MATERIAL_OPTIONS);
                    break;
                case 'meshMaterialAsset':
                case 'shaderMaterialAsset':
                    console.warn(`Material changes for ${type} is not supported yet`);
                    break;
                default:
                    assertUnreachable(type, 'Invalid material type');
            }
        });
    }
    updateMaterialProperties(materialSpec, material, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            const maps = yield this.loadTexturesMaps(materialSpec);
            for (const optionName of properties) {
                let optionValue = materialSpec[optionName];
                if (optionValue !== undefined) {
                    if (!MATERIAL_MAP_NAMES.includes(optionName)) {
                        if (optionName === 'color') {
                            optionValue = new Color(optionValue);
                        }
                        material[optionName] = optionValue;
                    }
                }
            }
            for (const [key, value] of Object.entries(maps)) {
                value.flipY = false;
                material[key] = value;
            }
        });
    }
    createPhysicalMaterial(spec) {
        return __awaiter(this, void 0, void 0, function* () {
            const maps = yield this.loadTexturesMaps(spec);
            const material = new MeshPhysicalMaterial(Object.assign({ color: spec.color, roughness: spec.roughness, metalness: spec.metalness }, maps));
            return material;
        });
    }
}
//# sourceMappingURL=MaterialsLibrary.js.map